import { Injectable } from '@angular/core';
import { DeviceTypeEnum, PmtPushNotificationsConfiguration, PushNotificationService, SubscriptionService } from '@premotec/pmt-push-notifications';
import { BehaviorSubject, catchError, combineLatest, from, map, Observable, of, skip, switchMap, throwError } from 'rxjs';
import { UserAuthorizationService } from '../../security/services/user-authorization.service';
import { SharedPushNotificationHelper } from '../helpers/shared-push-notification.helper';
import { SharedServiceWorkerService } from './shared-service-worker.service';
import { SharedToasterService } from './shared-toaster.service';
import { SharedPushCapabilitiesHelper } from '../helpers/shared-push-capabilities.helper';

@Injectable({
    providedIn: 'root'
})
export class SharedPushNotificationService {
    public subscriptionSubject$ = new BehaviorSubject<PushSubscription | null>(null);

    private readonly notificationPermissionGrantedKey = 'granted';
    private readonly notificationPermissionDefaultKey = 'default';
    private readonly notificationPermissionDeniedKey = 'denied';

    private isInitialProcessing = true;
    // defines if initial check is allready done
    private isUserLoggedIn = false;

    constructor(
        private pmtSubscriptionService: SubscriptionService,
        private pmtPushNotificationsConfiguration: PmtPushNotificationsConfiguration,
        private sharedToasterService: SharedToasterService,
        private userAuthorizationService: UserAuthorizationService,
        private sharedServiceWorkerService: SharedServiceWorkerService,
        private sharedPushNotificationHelper: SharedPushNotificationHelper,
        private pushNotificationService: PushNotificationService
    ) {
        if (!SharedPushCapabilitiesHelper.isPushSupportedOnThisDevice) {
            console.warn("Push notifications not supported on this device.");
            return; // stop all push logic
        }
        
        this.subscribeToEvents();
    }

    private subscribeToEvents(): void {

        if (!SharedPushCapabilitiesHelper.isPushSupportedOnThisDevice) {
            console.warn("Skipping push event subscriptions – push not supported.");
            return;
        }

        const EMIT_THRESHOLD = 1500; // ms
        let lastUpdateActiveUserSubscriptionEmit = 0;

        // used to update method only once if both events triggers
        const emitUpdateActiveUserSubscription = () => {
            const now = Date.now();
            if (now - lastUpdateActiveUserSubscriptionEmit > EMIT_THRESHOLD) {
                lastUpdateActiveUserSubscriptionEmit = now;
                this.updateActiveUserSubscription();
            }
        };


        this.sharedServiceWorkerService.isServiceWorkerReadySubject$.subscribe({
            next: (isReady: boolean) => {
                if (!isReady) return;
                emitUpdateActiveUserSubscription();
            }
        });

        this.userAuthorizationService.isLoggedIn().then((x) => {
            this.isUserLoggedIn = x;
        });

        this.userAuthorizationService.user$.subscribe(() => emitUpdateActiveUserSubscription())

        this.userAuthorizationService.loggedIn$
            .pipe(skip(1))
            .subscribe(loggedIn => {
                if (!loggedIn) this.disablePushNotification();
            });

        this.sharedServiceWorkerService.visibilitychange$.subscribe({
            next: (visibilitychanged: boolean) => {
                if (!visibilitychanged) return;
                emitUpdateActiveUserSubscription();
            }
        });

        combineLatest([
            this.subscriptionSubject$,
            this.userAuthorizationService.loggedIn$
        ])
        .subscribe(([subscription, loggedIn]) => {
            this.processSubscriptionChange(subscription, loggedIn);
        });
    }

    public get isNotificationAvaiable(): boolean {
        // (Notification !== undefined) will causes an ReferenceError
        if (typeof Notification === 'undefined') return false;
        return ('permission' in Notification);
    }

    public get isBrowserPermissionDefault(): boolean {
        return this.isPermissionAsExpected(this.notificationPermissionDefaultKey);
    }

    public get isBrowserPermissionGranted(): boolean {
        return this.isPermissionAsExpected(this.notificationPermissionGrantedKey);
    }

    public get isBrowserPermissionDenied(): boolean {
        return this.isPermissionAsExpected(this.notificationPermissionDeniedKey);
    }

    public get hasSubscription(): boolean {
        return this.subscriptionSubject$.value !== null;
    }

    public get subscriptionEndpoint(): string {
        if (!this.hasSubscription) return '';
        return this.subscriptionSubject$.value?.toJSON().endpoint ?? '';
    }

    public get subscriptionTime(): number {
        if (!this.hasSubscription) return 0;
        return this.subscriptionSubject$.value?.toJSON().expirationTime ?? 0;
    }

    public enablePushNotification(): void {
        try {
            if (!SharedPushCapabilitiesHelper.isPushSupportedOnThisDevice) {
                console.warn("Push not supported on this device.");
                return;
            }

            if (!this.sharedServiceWorkerService.isServiceWorkerAvailable()) return;

            this.pmtSubscriptionService.askUserPermission().subscribe({
                next: (granted) => {
                    // not granted by browser
                    if (!granted) return;
                    this.subscribeToPushNotification();
                },
                error: (e) => {
                    this.sharedToasterService.showGeneralError();
                }
            });

        } catch (e) {
            console.warn("enablePushNotification unsupported", e);
        }
    }

    public async disablePushNotification(): Promise<void> {
        try {
            if (!SharedPushCapabilitiesHelper.isPushSupportedOnThisDevice) return;
            if (!this.sharedServiceWorkerService.isServiceWorkerAvailable()) return;            
            this.unsubscribeFromPushNotificationTest().subscribe({
                next: (success) => {
                    if (!success) return;
                    this.isUserLoggedIn = false;
                    this.subscriptionSubject$.next(null);
                },
                error: () => {
                    this.sharedToasterService.showGeneralError();
                }
            });

        } catch (e) {
            console.warn("disablePushNotification unsupported", e);
        }
    }

    private unsubscribeFromPushNotificationTest(): Observable<boolean> {
        return from(navigator.serviceWorker.ready).pipe(
            switchMap(async reg => {
                const subscription = await reg.pushManager.getSubscription();

                if (subscription) {
                    await subscription.unsubscribe();
                    return subscription;
                }

                return null;
            }),
            switchMap(subscription => {
                if (!subscription) {
                    // Still attempt backend cleanup if needed
                    return of(true);
                }
    
                return this.pushNotificationService.deleteSubscription(subscription.toJSON()).pipe(
                switchMap(() =>
                    from(subscription.unsubscribe()).pipe(
                    map(() => true)
                    )
                ),
                catchError(err => {
                    console.error('Failed to remove subscription from server', err);
                    return throwError(() => new Error('Server error during unsubscription'));
                })
            );
        }),
        catchError(err => {
            // Catch errors from the outer observable (e.g., navigator.serviceWorker.ready)
            console.error('Unsubscribe flow error:', err);
            return throwError(() => err);
        })
        );
    }  

    public sendManualUpdateToServer(): void {
        if (!this.hasSubscription) return;
        this.sendUpdateToServer(this.subscriptionSubject$.value!);
    }

    private sendUpdateToServer(subscription: PushSubscription): void {
        try {
            if (!SharedPushCapabilitiesHelper.isPushSupportedOnThisDevice) return;
            if (!this.sharedServiceWorkerService.isServiceWorkerAvailable()) return;
            
            this.pmtSubscriptionService.sendSubscription(subscription, DeviceTypeEnum.Other).subscribe({
                next: (isSuccess) => {
                    if (!isSuccess) this.sharedToasterService.showGeneralError();
                },
                error: () => {
                    this.sharedToasterService.showGeneralError();
                }
            });

        } catch (e) {
            console.warn("sendUpdateToServer unsupported", e);
        }
    }

    private updateActiveUserSubscription(): void {
        try {
            if (!SharedPushCapabilitiesHelper.isPushSupportedOnThisDevice) return;
            if (!this.sharedServiceWorkerService.isServiceWorkerAvailable()) return;

            this.executeSpecificiOSLogic();
            this.pmtSubscriptionService.getUserSubscription().then(subscription => {
                this.subscriptionSubject$.next(subscription);
            });

        } catch (e) {
            console.warn("updateActiveUserSubscription unsupported", e);
        }
    }

    private isPermissionAsExpected(permissionKey: string): boolean {
        try {
            if (!this.isNotificationAvaiable) return false;

            return Notification.permission == permissionKey;
        } catch (e) {
            console.warn('Permission check failed', e);
            return false;
        }
    }

    private async processSubscriptionChange(subscription: PushSubscription | null, isUserLoggedIn: boolean): Promise<void> {
        if (!SharedPushCapabilitiesHelper.isPushSupportedOnThisDevice) return;

        if (this.isInitialProcessing) {
            this.isInitialProcessing = false;
            return;
        }

        if (!isUserLoggedIn) return;

        // nothing needs to be done. Maybe we need to call The "remove" endpoint for this state
        if (subscription == null && this.isBrowserPermissionDenied) return;

        // nothing needs to be done. User needs to be asked for permission with unser interaction first
        if (subscription == null && this.isBrowserPermissionDefault) return;

        if (subscription == null && this.isBrowserPermissionGranted) {

            const registration = await navigator.serviceWorker.ready;
            let actualSubscription = await registration.pushManager.getSubscription();

            for (let i = 0; i < 5 && !actualSubscription; i++) {
                await new Promise(r => setTimeout(r, 1000));
                actualSubscription = await registration.pushManager.getSubscription();
            }

            if (actualSubscription) {
                console.log('[Push] Restoring existing subscription');
                this.subscriptionSubject$.next(actualSubscription);
                return;
            }

            if (!actualSubscription) {

                console.log('[Push] No subscription found → creating new one');
                await this.subscribeToPushNotification();
                return;
            }
        }

        if (subscription == null) {
            console.error("subscription should not be null");
            return;
        }

        var isValid = this.sharedPushNotificationHelper.isSubscriptionKeyValid(subscription);

        if (!isValid) {
            this.unsubscribeFromPushNotification();
        }

        this.sendUpdateToServer(subscription);
    }

    // needs to be done if for example the vapid keys changes
    private unsubscribeFromPushNotification(): void {
        if (!SharedPushCapabilitiesHelper.isPushSupportedOnThisDevice) return;
        this.subscriptionSubject$.value?.unsubscribe().then(subscription => {
            this.subscriptionSubject$.next(null);
        });
    }

    private subscribeToPushNotification() {
        try {
            if (!SharedPushCapabilitiesHelper.isPushSupportedOnThisDevice) return;

           if (!this.sharedServiceWorkerService.isServiceWorkerAvailable()) return;
            this.pmtSubscriptionService.subscribeToPushNotification(this.pmtPushNotificationsConfiguration.vapidPublicKey).subscribe({
                next: (subscription) => {
                    this.isInitialProcessing = true;
                    this.subscriptionSubject$.next(subscription);
                },
                error: () => {
                    this.sharedToasterService.showGeneralError();
                }
            });

        } catch (e) {
            console.warn("subscribeToPushNotification unsupported", e);
        }
    }

    private executeSpecificiOSLogic(): void {
        try {
            if (!SharedPushCapabilitiesHelper.isIOS) return;
            if (SharedPushCapabilitiesHelper.isIOSOld) return;
            if (!this.isNotificationAvaiable) return;

            // only used in iOs to update notification.permission state
            this.pmtSubscriptionService.askUserPermission().subscribe({
                error: (e) => {
                    this.sharedToasterService.showGeneralError();
                }
            });

        } catch (e) {
            console.warn("executeSpecificiOSLogic unsupported", e);
        }
    }

    private get isIOS(): boolean {
        const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
        // first, use userAgentData if available
        const platform = (navigator as any).userAgentData?.platform;

        // userAgentData (only works in Chromium, not Safari)
        if (platform) return platform === 'iOS' || platform === 'iPadOS';

        // classic userAgent detection
        const isOldiOS = /iPad|iPhone|iPod/.test(userAgent);
        // fallback for iPadOS 13+ masquerading as macOS
        const isLikelyiPadOS = userAgent.includes('Macintosh') && 'ontouchend' in document;
        return isOldiOS || isLikelyiPadOS;
    }

}
